import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {Alert, Button, Card, Divider, Skeleton, Stack, Typography} from "@mui/material";
import Property from "crqlar-react-common/dist/components/Property";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {useAllServices} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import {Link as RouterLink} from "react-router-dom";
import {DigitalSigningTemplateService} from "./digitalSigningTemplate.service";
import DigitalSigningTipServiceDialog from "./DigitalSigningTipServiceDialog";

interface Props {
    offeredSectorIds: string[]
}

// Rows like the danger zone's: what it is on the left, the button on the right; one height with or without a button
const ROW_SX = {px: 1.5, py: 1.25, minHeight: 52};

// The service's details in a new tab: whose tips it takes is set there
const ServiceButton = ({service, label}: {service: Service, label?: string}) => {
    const {t} = useTranslation();
    return (
        <Button
            size={'small'}
            variant={'outlined'}
            component={RouterLink}
            to={`/system/services/${service.id}`}
            target={'_blank'}
            rel={'noopener noreferrer'}
            endIcon={<OpenInNewIcon sx={{fontSize: 16}}/>}
            sx={{flexShrink: 0, textTransform: 'none'}}
        >
            {label ?? (service.name || t('Service'))}
        </Button>
    );
};

// Opens the dialog that creates a tip service, or makes an existing service one; in an alert in its color
const SetUpButton = ({onClick, color}: {onClick: () => void, color?: 'inherit'}) => {
    const {t} = useTranslation();
    return (
        <Button size={'small'} variant={'outlined'} color={color} onClick={onClick} sx={{flexShrink: 0, textTransform: 'none'}}>
            {t('Set up')}
        </Button>
    );
};

interface SectorProps {
    sectorId: string
    sectorName?: string
    services: Service[]
}

function SectorTipServices({sectorId, sectorName, services}: SectorProps) {
    const {t, i18n} = useTranslation();
    const readOnly = useReadOnly();
    // Whose tip service the dialog sets up: a staff member's, or with none the default one
    const [setUp, setSetUp] = useState<{staff?: {resourceId: string, name: string}} | null>(null);
    // Set on the services: refetched when staff come back from a service's tab
    const query = useQuery(
        ['digitalSigningTipServices', sectorId],
        () => DigitalSigningTemplateService.tipServices(sectorId)
    );
    const tips = query.data;
    const serviceOf = (id?: string | null) => (id ? services.find(service => service.id === id) ?? {id} : undefined);

    const heading = !!sectorName && <Typography variant={'h6'}>{sectorName}</Typography>;
    if (!tips) {
        return (
            <Stack gap={2}>
                {heading}
                {query.isError ? (
                    <Alert
                        severity={'error'}
                        variant={'outlined'}
                        sx={{alignItems: 'center', py: 0}}
                        action={<Button color={'inherit'} size={'small'} onClick={() => query.refetch()}>{t('Retry')}</Button>}
                    >
                        {t('The tip services could not be loaded.')}
                    </Alert>
                ) : <Skeleton variant={'rounded'} height={52}/>}
            </Stack>
        );
    }

    // The tip service without staff takes the tips of everyone without their own
    const defaultService = serviceOf(tips.defaultServiceId);
    const staff = [...tips.staff].sort((left, right) => left.name.localeCompare(right.name, i18n.language, {sensitivity: 'base'}));

    return (
        <Stack gap={2}>
            {heading}
            <Property
                title={t('Default tip service')}
                tooltip={t('The tip service without staff: it takes the tips of all staff who have no tip service of their own.')}
            >
                {defaultService ? (
                    <Card variant={'outlined'} sx={{width: '100%'}}>
                        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={2} sx={ROW_SX}>
                            <Typography variant={'body1'} sx={{minWidth: 0}}>{defaultService.name || t('Service')}</Typography>
                            <ServiceButton service={defaultService} label={t('Open')}/>
                        </Stack>
                    </Card>
                ) : (
                    // Links for them are refused; as high as a row, its button where the rows have theirs
                    <Alert
                        severity={'error'}
                        sx={{width: '100%', minHeight: ROW_SX.minHeight, px: ROW_SX.px, py: 0, alignItems: 'center', '& .MuiAlert-action': {pt: 0, mr: 0}}}
                        action={readOnly ? undefined : <SetUpButton color={'inherit'} onClick={() => setSetUp({})}/>}
                    >
                        {t('Nobody receives the tip of staff with no direct tip: no default tip service is connected.')}
                    </Alert>
                )}
            </Property>

            {!!staff.length && (
                <Property
                    title={t('Staff')}
                    tooltip={t('Each staff member\'s own tip service: set on the service, under Takes the tips of.')}
                    labelAlignment={'top'}
                >
                    <Stack gap={1.5} sx={{width: '100%'}}>
                        <Alert severity={'info'}>
                            {t('If a staff member has no tip service of their own, their tip goes to the default tip service.')}
                        </Alert>
                        <Card variant={'outlined'}>
                            <Stack divider={<Divider/>}>
                                {staff.map(member => {
                                    const own = serviceOf(member.tipServiceId);
                                    return (
                                        <Stack key={member.resourceId} direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={2} sx={ROW_SX}>
                                            <Typography variant={'body1'} sx={{minWidth: 0}}>{member.name || t('Unknown')}</Typography>
                                            {own?.id ? <ServiceButton service={own}/> : (
                                                <Stack direction={'row'} alignItems={'center'} gap={1.5} sx={{flexShrink: 0}}>
                                                    <Typography variant={'body1'} color={'text.secondary'}>{t('No direct tip')}</Typography>
                                                    {!readOnly && <SetUpButton onClick={() => setSetUp({staff: {resourceId: member.resourceId, name: member.name}})}/>}
                                                </Stack>
                                            )}
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        </Card>
                    </Stack>
                </Property>
            )}

            <DigitalSigningTipServiceDialog open={!!setUp} sectorId={sectorId} staff={setUp?.staff} onClose={() => setSetUp(null)}/>
        </Stack>
    );
}

/**
 * Which service books a tip, per sector of the template: the default tip service, and each staff member's own -
 * set on the services (a tip service's linked resources), so every template of a sector shows the same. A
 * template offered in several sectors names each, as each has its own.
 */
const DigitalSigningTipServices = ({offeredSectorIds}: Props) => {
    const {t} = useTranslation();
    const sectors = useAllSectors();
    const services = useAllServices();

    if (!offeredSectorIds.length) {
        return (
            <Alert severity={'info'} variant={'outlined'} sx={{alignItems: 'center', py: 0}}>
                {t('Choose where the template is offered to see who gets the tip.')}
            </Alert>
        );
    }

    return (
        <Stack gap={{xs: 3, sm: 4}}>
            {offeredSectorIds.map(sectorId => (
                <SectorTipServices
                    key={sectorId}
                    sectorId={sectorId}
                    sectorName={offeredSectorIds.length > 1 ? sectors.find(sector => sector.id === sectorId)?.name || t('Unknown') : undefined}
                    services={services}
                />
            ))}
        </Stack>
    );
};

export default DigitalSigningTipServices;
